import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Cell, Legend,
} from "recharts";

const COLORS = ["#1E4D2B", "#F59E0B", "#14361E", "#16A34A", "#D97706", "#57534E"];

const Analytics = () => {
  const { t } = useLang();
  const [prices, setPrices] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedMandi, setSelectedMandi] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/prices"),
      api.get("/prices/history", { params: { days: 60 } }),
    ])
      .then(([p, h]) => {
        const seedPrices = p.data.prices.filter((x) => x.id); // seed prices have ids
        setPrices(p.data.prices);
        setHistory(h.data.history);
        const mandiList = Array.from(new Set(h.data.history.map((x) => x.mandi_name)));
        if (mandiList.length) setSelectedMandi(mandiList[0]);
        if (!seedPrices.length) { /* noop */ }
      })
      .finally(() => setLoading(false));
  }, []);

  const mandis = useMemo(() => Array.from(new Set(history.map((h) => h.mandi_name))), [history]);

  const trendData = useMemo(() => {
    return history
      .filter((h) => h.mandi_name === selectedMandi)
      .map((h) => ({ date: h.entry_date.slice(5), price: h.avg_price }));
  }, [history, selectedMandi]);

  const comparisonData = useMemo(() => {
    return prices.slice(0, 8).map((p) => ({
      name: p.mandi_name,
      price: p.avg_price,
    }));
  }, [prices]);

  return (
    <div data-testid="analytics-page" className="container-gc px-4 sm:px-6 py-8 sm:py-12">
      <h1 className="text-3xl sm:text-4xl font-bold text-stone-900">{t.analytics.title}</h1>
      <p className="text-stone-600 mt-1 mb-8">{t.analytics.subtitle}</p>

      {loading ? (
        <div className="card-gc p-12 text-center text-stone-500">{t.common.loading}</div>
      ) : (
        <>
          {/* Price Trend */}
          <div className="card-gc p-5 sm:p-6 mb-6" data-testid="trend-chart-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h2 className="text-xl font-semibold text-stone-900">{t.analytics.avg_price}</h2>
              <select
                data-testid="trend-mandi-select"
                value={selectedMandi}
                onChange={(e) => setSelectedMandi(e.target.value)}
                className="input-gc sm:w-56"
              >
                {mandis.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="h-72 sm:h-96">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="date" stroke="#57534E" fontSize={12} />
                  <YAxis stroke="#57534E" fontSize={12} domain={['dataMin - 10', 'dataMax + 10']} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4", fontSize: 13 }}
                    formatter={(v) => [`₹${v}/kg`, "Avg Price"]}
                  />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="#1E4D2B"
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 6, fill: "#F59E0B" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Mandi Comparison */}
          <div className="card-gc p-5 sm:p-6" data-testid="comparison-chart-card">
            <h2 className="text-xl font-semibold text-stone-900">{t.analytics.compare_title}</h2>
            <p className="text-stone-600 text-sm mt-1 mb-4">{t.analytics.compare_sub}</p>
            <div className="h-80 sm:h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparisonData} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="name" stroke="#57534E" fontSize={11} angle={-25} textAnchor="end" height={60} />
                  <YAxis stroke="#57534E" fontSize={12} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4", fontSize: 13 }}
                    formatter={(v) => [`₹${v}/kg`, "Avg"]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="price" name="Avg Price ₹/kg" radius={[8, 8, 0, 0]}>
                    {comparisonData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;
